import {
  App,
  convertProduktNameToTaasProduktName,
  Produktlinie as FrontendProduktlinie
} from '@ps20-frontend/frontend-models';
import {
  BasisStreckenState,
  DoRequestResponse,
  GlobalActions,
  GlobalPut,
  ProduktVariantInput,
  TaasNachlassVorteilReceivedPayload
} from '@ps20-frontend/shop-integration-redux';
import {
  Feature,
  GisDaten,
  Produktlinie,
  Produktlinienname,
  SalesProduktVariante,
  TaasNachlassVorteil,
  TaasRequestModel,
  TaasResponseModel
} from '@ps20/bff-models';
import { call, cancel, put, select } from 'redux-saga/effects';
import { PS20Logger } from '@ps20-frontend/logging';
import { CallEffect, CancelEffect, PutEffect, SelectEffect } from '@redux-saga/core/effects'; // NOSONAR
import { selectFeatureToggle, selectPreisverzeichnisTestValue } from '@ps20-frontend/selector';
import { getSelectedProduktlinie } from '../preis.selector';
import { TaasData } from '../taasRequestHelper';
import { handleDeltafaktorenOutOfRange, setDeltafaktorenIfAvailable } from '../deltafaktorenHandler';
import { updateBausteineWithTarifierungsergebnis } from '../taasResponseHelper';

export interface TarifierungRequestContext {
  requestedForProduktlinie?: Produktlinienname;
  requestedForKurztext?: string;
  requestNumber?: number;
}

function getStaleReason(
  requestContext: TarifierungRequestContext,
  selectedProduktlinie: FrontendProduktlinie,
): string {
  if (
    requestContext?.requestedForProduktlinie != null &&
    requestContext.requestedForProduktlinie !== selectedProduktlinie?.produktName
  ) {
    return 'produktlinie';
  }
  if (
    requestContext?.requestedForKurztext != null &&
    selectedProduktlinie?.kurztext != null &&
    requestContext.requestedForKurztext !== selectedProduktlinie.kurztext
  ) {
    return 'produkttabelle';
  }
  return null;
}

enum Versicherung {
  WOHNGEBAEUDEVERSICHERUNG = 'Wohngebäudeversicherung',
  HAUSRATVERSICHERUNG = 'Hausratversicherung',
  HAFTPFLICHTVERSICHERUNG = 'Haftpflichtversicherung',
  RECHTSCHUTZVERSICHERUNG = 'Rechtsschutzversicherung',
  RECHTSCHUTZVERSICHERUNG_VERMIETUNG = 'Rechtsschutzversicherung-Vermieter',
  TIERHALTERHAFTPFLICHTVERSICHERUNG_HUND = 'Tierhalter-Haftpflicht (Hund)',
  TIERHALTERHAFTPFLICHTVERSICHERUNG_PFERD = 'Tierhalter-Haftpflicht (Pferd)',
  TIERKRANKENVERSICHERUNG = 'Tierkrankenversicherung',
  UNFALLVERSICHERUNG = 'Unfallversicherung',
  JAGDHAFTPFLICHTVERSICHERUNG = 'Jagd-Haftpflichtversicherung',
  GEGENSTANDSSCHUTZVERSICHERUNG = 'Gegenstandsschutzversicherung',
}

export class ShopTarifierungsErgebnisHandler<T extends BasisStreckenState, U extends TaasData> {
  constructor(
    private readonly appId: App,
    private readonly getVereinbarungInfoText: (
      state: T,
    ) => string = ShopTarifierungsErgebnisHandler.defaultInfoText,
  ) {
    this.verarbeiteTarifierungsErgebnis = this.verarbeiteTarifierungsErgebnis.bind(this);
    this.setBeitrageInShop = this.setBeitrageInShop.bind(this);
    this.getRelevantSalesproduktVariante = this.getRelevantSalesproduktVariante.bind(this);
    this.setPreisverzeichnis = this.setPreisverzeichnis.bind(this);
    this.setZuschlagRisiko = this.setZuschlagRisiko.bind(this);
    this.setVereinbarungen = this.setVereinbarungen.bind(this);
    this.setBeitraegeInStrecke = this.setBeitraegeInStrecke.bind(this);
    this.showErrorPage = this.showErrorPage.bind(this);
    this.setGisDaten = this.setGisDaten.bind(this);
  }

  private static defaultInfoText(): string {
    return '';
  }

  *verarbeiteTarifierungsErgebnis(
    successAction: DoRequestResponse<TaasResponseModel, TaasRequestModel<U>>,
  ): Generator<CallEffect | SelectEffect> {
    const state: T = (yield select()) as T;
    const taasResponseModel = successAction.payload;
    const produktlinien = taasResponseModel.produktlinien;

    const requestContext = successAction.doRequestAction?.payload
      ?.additionalData as TarifierungRequestContext;
    const selectedProduktlinie = getSelectedProduktlinie(state.produkt);
    const selectedProduktlinienname = selectedProduktlinie?.produktName;
    const staleReason = getStaleReason(requestContext, selectedProduktlinie);
    if (staleReason) {
      PS20Logger.info(
        `${this.appId}.verarbeiteTarifierungsErgebnis.staleResponseDiscarded`,
        `Tarifierungsantwort verworfen (${staleReason}): angefordert fuer produktlinie=${requestContext?.requestedForProduktlinie} kurztext=${requestContext?.requestedForKurztext}, aktuell produktlinie=${selectedProduktlinienname} kurztext=${selectedProduktlinie?.kurztext} (angebotsId=${state.angebotsId})`,
        this.appId,
      );
      return;
    }

    if (this.appId === App.TKV_V2) {
      const responseMatchesCurrentSelection = produktlinien?.some(
        produktlinie =>
          produktlinie.salesProductName ===
          convertProduktNameToTaasProduktName(selectedProduktlinienname),
      );
      if (!responseMatchesCurrentSelection) {
        PS20Logger.info(
          'TKV_V2.verarbeiteTarifierungsErgebnis.staleResponse',
          `Tarifierungsantwort passt nicht zur aktuell ausgewählten Produktlinie (angebotsId=${state.angebotsId}) - vermutlich Race Condition, keine garantierte Fehlerursache`,
          App.TKV_V2,
        );
      }
    }

    const salesProduktVariante = this.getRelevantSalesproduktVariante(
      state.rabattstufe,
      produktlinien,
      state,
    );
    const salesProduktVarianteOhneKombiRabatt = this.getRelevantSalesproduktVariante(
      0,
      produktlinien,
      state,
    );
    if (salesProduktVariante && salesProduktVarianteOhneKombiRabatt) {
      const gisDaten = successAction.payload.gisDaten;
      const nachlassTb = successAction.doRequestAction.payload.body.nachlassTb;

      if (!state.vertragsoptionen?.beibehalten) {
        yield call(this.setGisDaten, gisDaten);
      }
      yield call(this.setBeitraegeInStrecke, salesProduktVariante, successAction);
      yield call(this.setPreisverzeichnis, produktlinien);
      yield call(this.setVereinbarungen, produktlinien, salesProduktVariante, nachlassTb);
      yield call(this.setZuschlagRisiko, taasResponseModel);
      yield call(setDeltafaktorenIfAvailable, successAction.payload.deltafaktoren);
      yield call(handleDeltafaktorenOutOfRange, successAction.payload.deltafaktoren, {
        appId: this.appId,
        angebotsId: state.angebotsId,
      });
      yield call(this.setBeitrageInShop, salesProduktVariante, salesProduktVarianteOhneKombiRabatt);
    } else {
      yield call(this.showErrorPage);
    }
  }

  private *setGisDaten(gisDaten: GisDaten): Generator<PutEffect> {
    return gisDaten && (yield put(new GlobalActions().gisDatenErmittelt(gisDaten)));
  }

  private *showErrorPage(): Generator<CallEffect | CancelEffect> {
    const globalPut = GlobalPut.getInstance();
    yield call(globalPut.put, new GlobalActions().errorPageRequested());
    PS20Logger.error(
      this.appId,
      new Error('Bruttopreis konnte nicht auf Produktlinie gemapped werden'),
    );
    yield cancel();
  }

  private *setBeitrageInShop(
    salesProduktVariante: SalesProduktVariante,
    salesProduktVarianteOhneKombiRabatt: SalesProduktVariante,
  ): Generator<CallEffect | SelectEffect> {
    const state: T = (yield select()) as T;
    const globalPut = GlobalPut.getInstance();
    yield call(
      globalPut.put,
      new GlobalActions().preiseInsDashboardUebernehmen({
        appId: this.appId,
        angebotsId: state.angebotsId,
        bruttopreis: salesProduktVariante.gesamtBruttopreisFuerZahlweise,
        bruttopreisOhneKombiRabatt:
          salesProduktVarianteOhneKombiRabatt.gesamtBruttopreisFuerZahlweise,
      }),
    );
  }

  private getRelevantSalesproduktVariante(
    zaehlstuecke: number,
    produktlinien: Produktlinie[],
    state: T,
  ): SalesProduktVariante {
    const produktlinienname = getSelectedProduktlinie(state.produkt)?.produktName;
    return produktlinien
      .find(
        produktlinie =>
          produktlinie.salesProductName === convertProduktNameToTaasProduktName(produktlinienname),
      )
      ?.salesProduktVariants.find(
        variant =>
          variant.zahlweise === state.vertragsoptionen.zahlungsweise &&
          variant.zaehlstuecke === Math.min(zaehlstuecke, 5) &&
          variant.selbstbeteiligung == state.vertragsoptionen.selbstbeteiligung,
      );
  }

  private *setZuschlagRisiko(
    taaResponseModel: TaasResponseModel,
  ): Generator<SelectEffect | CallEffect> {
    const globalPut = GlobalPut.getInstance();
    const state: T = (yield select()) as T;
    yield call(
      globalPut.put,
      new GlobalActions().zuschlagrisikoReceived({
        zuschlagRisiko: this.appId === App.UNF ? null : taaResponseModel.zuschlagRisiko ?? 0,
        appId: this.appId,
        angebotsId: state.angebotsId,
      }),
    );
  }

  private *setPreisverzeichnis(
    produktlinienFromResponse: Array<Produktlinie>,
  ): Generator<SelectEffect | PutEffect> {
    const state: T = (yield select()) as T;
    const selectedProduktlinieFromState = getSelectedProduktlinie(state.produkt);
    const selectedProduktlinieFromResponse = produktlinienFromResponse.find(produktlinie => {
      return (
        convertProduktNameToTaasProduktName(selectedProduktlinieFromState.produktName) ===
        produktlinie.salesProductName
      );
    });

    if (selectedProduktlinieFromResponse?.preisverzeichnis == null) {
      PS20Logger.error(
        this.appId,
        'Tarifierung.preisverzeichnisFehlt',
        JSON.stringify({
          angebotsId: state.angebotsId,
          produktName: selectedProduktlinieFromState?.produktName,
          kurztext: selectedProduktlinieFromState?.kurztext,
          beibehalten: state.vertragsoptionen?.beibehalten,
        }),
      );
    }

    if (
      selectedProduktlinieFromResponse?.preisverzeichnis !==
        selectedProduktlinieFromState.preisverzeichnis &&
      !state.vertragsoptionen?.beibehalten
    ) {
      let preisverzeichnis = selectedProduktlinieFromResponse.preisverzeichnis;
      if (selectFeatureToggle(state, Feature.PVZ_ERSATZGESCHAEFT_MOCK)) {
        preisverzeichnis = selectPreisverzeichnisTestValue(state);
      }
      yield put(
        new GlobalActions().preisverzeichnisChanged({
          preisverzeichnis,
        }),
      );
    }
  }

  private *setVereinbarungen(
    produktlinien: Produktlinie[],
    salesProduktVariante: SalesProduktVariante,
    nachlassTb: number,
  ): Generator<SelectEffect | CallEffect> {
    const globalPut = GlobalPut.getInstance();
    const state: T = (yield select()) as T;

    const rbdMax: number = produktlinien[0].rbdMax;
    if (rbdMax != null && !state.vertragsoptionen.beibehalten) {
      yield call(
        globalPut.put,
        new GlobalActions().rbdMaxReceived({
          appId: this.appId,
          angebotsId: state.angebotsId,
          rbdMax,
          versicherung: this.getVersicherung(),
          info: this.getVereinbarungInfoText(state),
          withRole: state.transientState.role,
        }),
      );
    }
    const taasNachlassVorteil: TaasNachlassVorteil = produktlinien[0].taasNachlassVorteil;
    const inputForNachlassVorteilRequest: Array<ProduktVariantInput> =
      produktlinien[0].salesProduktVariants.map(prodVar => {
        return {
          zahlweise: prodVar.zahlweise,
          sb: prodVar.selbstbeteiligung,
          laufzeit: prodVar.laufzeit,
          cpSternSum:
            prodVar.bausteinPreise && prodVar.bausteinPreise.length > 0
              ? prodVar.bausteinPreise
                  .map(bp => bp?.cpStern)
                  .reduce((sum, cpStern) => sum + (cpStern || 0), 0)
              : 0,
          jahresNettoBausteineSum:
            prodVar.bausteinPreise && prodVar.bausteinPreise.length > 0
              ? salesProduktVariante.bausteinPreise
                  .map(bp => bp?.jahresNetto)
                  .reduce((sum, jahresNetto) => sum + (jahresNetto || 0), 0)
              : 0,
        };
      });
    if (!state.vertragsoptionen.beibehalten && taasNachlassVorteil) {
      const payload: TaasNachlassVorteilReceivedPayload = {
        appId: this.appId,
        angebotsId: state.angebotsId,
        taasNachlassVorteil,
        salesProduktVariants: inputForNachlassVorteilRequest,
      };
      const resetNachlass = taasNachlassVorteil.max < taasNachlassVorteil.nachlass;
      if (resetNachlass) {
        payload.taasNachlassVorteil.nachlass = payload.taasNachlassVorteil.max;
      }
      yield call(globalPut.put, new GlobalActions().taasNachlassRabattReceived(payload));
      if (resetNachlass) {
        yield call(globalPut.put, new GlobalActions().updateReceivedVorteilNachlass());
        yield call(globalPut.put, new GlobalActions().vorteilsRabattHinweisChanged(true));
      }
    }
    const treuebonusMax: number = salesProduktVariante.treuebonus;
    if (nachlassTb == null && !state.vertragsoptionen.beibehalten) {
      yield call(
        globalPut.put,
        new GlobalActions().treuebonusMaxReceived({
          appId: this.appId,
          angebotsId: state.angebotsId,
          treuebonusMax,
          versicherung: this.getVersicherung(),
          info: this.getVereinbarungInfoText(state),
        }),
      );
    }
    const nachlass: number = produktlinien[0].nachlassAktion;
    if (nachlass) {
      yield call(
        globalPut.put,
        new GlobalActions().nachlassReceived({
          appId: this.appId,
          angebotsId: state.angebotsId,
          aktionsNachlass: nachlass,
          code: state.vertragsoptionen.selectedKooperation,
        }),
      );
    }
  }

  private getVersicherung(): Versicherung {
    return ({
      [App.VWG]: Versicherung.WOHNGEBAEUDEVERSICHERUNG,
      [App.VWG_V2]: Versicherung.WOHNGEBAEUDEVERSICHERUNG,
      [App.VHV]: Versicherung.HAUSRATVERSICHERUNG,
      [App.PH]: Versicherung.HAFTPFLICHTVERSICHERUNG,
      [App.RS]: Versicherung.RECHTSCHUTZVERSICHERUNG,
      [App.RS_VERMIETUNG]: Versicherung.RECHTSCHUTZVERSICHERUNG_VERMIETUNG,
      [App.THH_HUND]: Versicherung.TIERHALTERHAFTPFLICHTVERSICHERUNG_HUND,
      [App.THH_PFERD]: Versicherung.TIERHALTERHAFTPFLICHTVERSICHERUNG_PFERD,
      [App.TKV]: Versicherung.TIERKRANKENVERSICHERUNG,
      [App.TKV_V2]: Versicherung.TIERKRANKENVERSICHERUNG,
      [App.UNF]: Versicherung.UNFALLVERSICHERUNG,
      [App.JHP]: Versicherung.JAGDHAFTPFLICHTVERSICHERUNG,
      [App.GEGE]: Versicherung.GEGENSTANDSSCHUTZVERSICHERUNG,
    } as Partial<Record<App, Versicherung>>)[this.appId];
  }

  private *setBeitraegeInStrecke(
    salesProduktVariante: SalesProduktVariante,
    successAction: DoRequestResponse<TaasResponseModel, TaasRequestModel<U>>,
  ): Generator<SelectEffect | PutEffect> {
    const state: T = (yield select()) as T;
    yield put(
      new GlobalActions().jahresbeitragChanged({
        jahresbeitrag: salesProduktVariante.gesamtBruttopreisJaehrlich,
      }),
    );
    yield put(
      new GlobalActions().gesamtpreisMitRabattChanged(
        salesProduktVariante.gesamtBruttopreisFuerZahlweise,
      ),
    );
    yield put(
      new GlobalActions().shopTarifierungBausteinpreiseChanged(salesProduktVariante.bausteinPreise),
    );
    if (state.vertragsoptionen?.beibehalten) {
      const tarifierteProduktlinien = updateBausteineWithTarifierungsergebnis(
        state,
        successAction.payload.produktlinien,
        this.appId,
      );
      yield put(
        new GlobalActions().preiseStreckeErmittelt(
          this.appId,
          tarifierteProduktlinien,
          successAction.payload,
          state.angebotsId,
        ),
      );
    }
  }
}
